/**
 * Главная: место, куда можно вернуться откуда угодно.
 *
 * До неё выйти из занятия было нельзя: выбрав на первом входе «с основ»,
 * пользователь оставался в основах, потому что дальше экрана занятия ничего не
 * предлагалось. Экран отвечает на три вопроса сразу: продолжить начатое,
 * заняться другим режимом или поменять то, с чего начал.
 *
 * Показывает всё, чем можно заняться (ТЗ FR-EN-53): продолжение начатого,
 * доступные режимы, контрольный тест, отчёт и аккаунт. Список режимов здесь и
 * на экране результата — один и тот же: два списка разошлись бы при первом же
 * новом режиме.
 *
 * Разобран по разделам, а не выложен стеной кнопок (ТЗ FR-EN-82): шестнадцать
 * кнопок подряд одинакового веса не отвечают ни на один вопрос — продолжение
 * занятия стояло в них рядом с пунктуацией и с сохранением прогресса.
 *
 * Продолжение начатого и рисунок стоят рядом, а не друг под другом, и выбор
 * пути обучения раскрывается по требованию (ТЗ FR-EN-85): рисунок над кнопкой
 * отодвигал главное действие экрана на пол-оборота колеса вниз, а путь
 * обучения меняют раз в несколько месяцев.
 */

import { Button, Card, ChoiceGroup, KeyCap, KeyboardArt, ProgressBar } from '@type2x/ui';
import { START_PATHS, type StartPath } from '@type2x/feature-personal-workspace';

import { LockedHint, ModeMenu, type ModeMenuProps } from './ModeMenu.js';
import { KeyboardNotice } from './KeyboardNotice.js';
import { useKeyboardPresence } from './keyboard-presence.js';

export interface HomeScreenProps extends ModeMenuProps {
  readonly onContinue: () => void;
  readonly onOpenReport: () => void;
  readonly onOpenAchievements: () => void;
  /** Открывает рейтинг: он живёт на сервере и требует аккаунта. */
  readonly onOpenLeaderboard: () => void;
  /** Показывать ли игровые элементы (ТЗ FR-GM-08). */
  readonly gameElements: boolean;
  readonly onSaveProgress: () => void;
  readonly onStartControlTest: () => void;
  /** С чего пользователь начал обучение этому языку. */
  readonly startPath: StartPath;
  /** Освоение собственной раскладки: измеримый ответ на «сколько я прошёл». */
  readonly keys: { readonly mastered: number; readonly total: number };
  readonly onStartPathChange: (path: StartPath) => void;
}

export function HomeScreen(props: HomeScreenProps): React.JSX.Element {
  const { t, onContinue, keys } = props;

  /*
   * Устройству без клавиатуры тренировка недоступна, и главная это признаёт:
   * сначала объяснение и то, что работает, — прогресс, отчёт, достижения,
   * аккаунт, — и только потом режимы, которые ему не откроются
   * (Type2X_MASTER_PROMPT.md §7). Порядок на компьютере не меняется.
   */
  const touchOnly = useKeyboardPresence() === 'touch-only';

  /*
   * Куда ведёт продолжение: новичок, начавший с основ, возвращается в них,
   * пока там осталось что открывать; остальные — в персональную тренировку.
   * То же решение принимает `resumeScreen`; здесь оно только называется
   * словом, чтобы кнопка не вела в неизвестность.
   */
  const target =
    props.startPath === 'fundamentals' && props.fundamentalsAvailable
      ? t('fundamentals.title')
      : t('practice.title');

  return (
    <Card title={t('home.title')}>
      <KeyboardNotice t={t} />
      {touchOnly && <LookSection {...props} />}

      {/*
        Карточка продолжения — единственный крупный объект экрана (ТЗ UI-41):
        куда ведёт, сколько уже пройдено и одно действие. Заливкой на этом
        экране пользуется только она.
      */}
      <div className="t2x-home__hero">
        <div className="t2x-home__lead">
          <p className="t2x-muted">{t('home.explain')}</p>

          <p className="t2x-home__target">{t('home.continueTo', { mode: target })}</p>

          <p className="t2x-muted t2x-home__measure">
            {t('home.mastered', { mastered: keys.mastered, total: keys.total })}
          </p>

          <ProgressBar
            value={keys.total === 0 ? 0 : keys.mastered / keys.total}
            label={t('home.mastered', { mastered: keys.mastered, total: keys.total })}
          />

          <div className="t2x-home__primary">
            <Button onClick={onContinue}>{t('home.continue')}</Button>
          </div>
        </div>

        {/* Рисунок стоит один раз и сбоку: он показывает долю освоенного, а
            точное число говорит подпись под ним (ТЗ UI-67). */}
        <figure className="t2x-home__art">
          <KeyboardArt mastered={keys.mastered} total={keys.total} />
          <figcaption className="t2x-muted t2x-home__legend">{t('home.legend')}</figcaption>
        </figure>
      </div>

      <ModeMenu {...props} />

      <LockedHint {...props} />

      {!touchOnly && <LookSection {...props} />}

      {/* Выбор пути обучения делается на первом входе, но однажды выбранный он
          не должен запирать: новичок, начавший с диагностики, вправе уйти в
          основы, а тот, кто уже печатает, — не сидеть в них. Раскрытие, а не
          постоянный блок: за этим приходят редко, а место оно занимает
          каждый раз. */}
      <details className="t2x-home__tune">
        <summary>{t('home.tune')}</summary>
        <ChoiceGroup
          legend={t('home.startPath')}
          name="start-path"
          value={props.startPath}
          options={START_PATHS.map((value) => ({
            value,
            label: t(`onboarding.start.${value}`),
          }))}
          onChange={(value: StartPath) => props.onStartPathChange(value)}
        />
      </details>
    </Card>
  );
}

/**
 * «Посмотреть и сохранить»: то, что работает и без клавиатуры.
 *
 * Отдельным компонентом, потому что стоит в двух местах: на компьютере — под
 * режимами, на устройстве без клавиатуры — прямо под объяснением, потому что
 * там это единственное, что человеку доступно.
 */
function LookSection(props: HomeScreenProps): React.JSX.Element {
  const { t, onOpenReport, onSaveProgress, onStartControlTest } = props;

  return (
    <section className="t2x-modes__group">
      <h3 className="t2x-modes__title">
        <KeyCap glyph="i" />
        {t('home.look')}
      </h3>
      <div className="t2x-modes__items">
        <Button variant="secondary" onClick={onStartControlTest}>
          {t('control.start')}
        </Button>
        <Button variant="ghost" onClick={onOpenReport}>
          {t('report.open')}
        </Button>
        {/* Выключенные игровые элементы исчезают целиком, а не превращаются
            в кнопку, ведущую к пустому экрану (ТЗ FR-GM-08). */}
        {props.gameElements && (
          <Button variant="ghost" onClick={props.onOpenAchievements}>
            {t('achievements.open')}
          </Button>
        )}
        <Button variant="ghost" onClick={props.onOpenLeaderboard}>
          {t('leaderboard.title')}
        </Button>
        <Button variant="ghost" onClick={onSaveProgress}>
          {t('account.title')}
        </Button>
      </div>
    </section>
  );
}
